import {
	resolveMobileSection,
	type MobileSection,
} from "./mobileNavigation.ts";

export type MobileReturnState = {
	activeTab?: string;
	archiveSeason?: string;
	filterKey?: string;
	filterValue?: string;
	presentation?: "sheet";
	readonlyReason?: string;
	returnHref?: string;
	returnLabel?: string;
	returnUrl?: string;
	scrollY?: number;
	sourceLabel?: string;
	sourcePage?: string;
	sourceSection?: MobileSection;
};

export type MobileReturnStateInput = {
	activeTab?: string;
	archiveSeason?: boolean | number | string | null;
	captureCurrentReturnUrl?: boolean;
	filterKey?: string;
	filterValue?: boolean | number | string | null;
	originSection?: MobileSection;
	pageID?: unknown;
	playerTid?: number | null;
	presentation?: "sheet";
	readonlyReason?: string;
	returnHref?: string;
	returnLabel?: string;
	returnUrl?: string;
	scrollY?: number;
	sourceLabel?: string;
	sourcePage?: string;
};

const mobileSections = new Set<MobileSection>([
	"home",
	"team",
	"games",
	"market",
	"league",
]);

const isMobileSection = (value: unknown): value is MobileSection =>
	typeof value === "string" && mobileSections.has(value as MobileSection);

const cleanToken = (value: unknown, maxLength = 80) => {
	if (typeof value !== "string") {
		return undefined;
	}

	const trimmed = value.trim();
	if (trimmed.length === 0) {
		return undefined;
	}

	return trimmed.slice(0, maxLength);
};

const cleanScrollY = (value: unknown) => {
	if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
		return undefined;
	}

	return Math.round(value);
};

const mobileReturnParamKeys = ["m_return", "m_return_label"] as const;

const cleanReturnUrl = (value: unknown) => {
	if (typeof value !== "string") {
		return undefined;
	}

	const trimmed = value.trim();
	if (!trimmed.startsWith("/") || trimmed.startsWith("//")) {
		return undefined;
	}

	try {
		const url = new URL(trimmed, "https://mobile-return.invalid");
		if (url.origin !== "https://mobile-return.invalid") {
			return undefined;
		}
		for (const key of mobileReturnParamKeys) {
			url.searchParams.delete(key);
		}

		const normalized = `${url.pathname}${url.search}${url.hash}`;
		return normalized.slice(0, 500);
	} catch {
		return undefined;
	}
};

export const getCurrentMobileReturnUrl = () => {
	if (typeof window === "undefined") {
		return undefined;
	}

	const { hash, pathname, search } = window.location;
	if (typeof pathname !== "string" || pathname.length === 0) {
		return undefined;
	}

	return cleanReturnUrl(`${pathname}${search ?? ""}${hash ?? ""}`);
};

export const resolveMobileSourceSection = (
	pageID: unknown,
	context?: Pick<MobileReturnStateInput, "originSection" | "playerTid">,
) => {
	const section = resolveMobileSection(pageID, context);
	return isMobileSection(section) ? section : undefined;
};

export const createMobileReturnState = ({
	activeTab,
	archiveSeason,
	captureCurrentReturnUrl = true,
	filterKey,
	filterValue,
	originSection,
	pageID,
	playerTid,
	presentation,
	readonlyReason,
	returnHref,
	returnLabel,
	returnUrl,
	scrollY,
	sourceLabel,
	sourcePage,
}: MobileReturnStateInput): MobileReturnState => {
	const state: MobileReturnState = {};
	const sourceSection =
		originSection ??
		resolveMobileSourceSection(pageID, {
			playerTid,
		});

	if (sourceSection) {
		state.sourceSection = sourceSection;
	}

	const cleanSourcePage = cleanToken(sourcePage);
	if (cleanSourcePage) {
		state.sourcePage = cleanSourcePage;
	}

	const cleanSourceLabel = cleanToken(sourceLabel);
	if (cleanSourceLabel) {
		state.sourceLabel = cleanSourceLabel;
	}

	const cleanActiveTab = cleanToken(activeTab);
	if (cleanActiveTab) {
		state.activeTab = cleanActiveTab;
	}

	const cleanFilterKey = cleanToken(filterKey, 40);
	if (cleanFilterKey && filterValue !== null && filterValue !== undefined) {
		state.filterKey = cleanFilterKey;
		state.filterValue = String(filterValue).slice(0, 120);
	}

	if (presentation === "sheet") {
		state.presentation = "sheet";
	}

	const cleanScroll = cleanScrollY(scrollY);
	if (cleanScroll !== undefined) {
		state.scrollY = cleanScroll;
	}

	const cleanReadonlyReason = cleanToken(readonlyReason, 120);
	if (cleanReadonlyReason) {
		state.readonlyReason = cleanReadonlyReason;
	}

	const cleanArchiveSeason = cleanToken(
		archiveSeason === null || archiveSeason === undefined
			? undefined
			: String(archiveSeason),
		40,
	);
	if (cleanArchiveSeason) {
		state.archiveSeason = cleanArchiveSeason;
	}

	const cleanReturn =
		cleanReturnUrl(returnHref) ??
		cleanReturnUrl(returnUrl) ??
		(captureCurrentReturnUrl ? getCurrentMobileReturnUrl() : undefined);
	if (cleanReturn) {
		state.returnHref = cleanReturn;
		state.returnUrl = cleanReturn;
	}

	const cleanReturnLabel = cleanToken(returnLabel);
	if (cleanReturn && cleanReturnLabel) {
		state.returnLabel = cleanReturnLabel;
	}

	return state;
};

export const encodeMobileReturnState = (state: MobileReturnState) => {
	const params = new URLSearchParams();

	if (state.sourceSection) {
		params.set("m_src", state.sourceSection);
	}
	if (state.sourcePage) {
		params.set("m_src_page", state.sourcePage);
	}
	if (state.sourceLabel) {
		params.set("m_src_label", state.sourceLabel);
	}
	if (state.activeTab) {
		params.set("m_tab", state.activeTab);
	}
	if (state.presentation === "sheet") {
		params.set("m_view", "sheet");
	}
	if (state.filterKey && state.filterValue !== undefined) {
		params.set("m_filter", `${state.filterKey}:${state.filterValue}`);
	}
	if (state.scrollY !== undefined) {
		params.set("m_scroll", String(state.scrollY));
	}
	if (state.readonlyReason) {
		params.set("m_readonly", state.readonlyReason);
	}
	if (state.archiveSeason) {
		params.set("m_archive", state.archiveSeason);
	}
	const returnHref = state.returnHref ?? state.returnUrl;
	if (returnHref) {
		params.set("m_return", returnHref);
	}
	if (state.returnLabel) {
		params.set("m_return_label", state.returnLabel);
	}

	return params.toString();
};

export const parseMobileReturnState = (
	input: string | URLSearchParams,
): MobileReturnState => {
	const params =
		typeof input === "string"
			? new URLSearchParams(input.startsWith("?") ? input.slice(1) : input)
			: input;
	const state: MobileReturnState = {};
	const sourceSection = params.get("m_src");

	if (isMobileSection(sourceSection)) {
		state.sourceSection = sourceSection;
	}

	const sourcePage = cleanToken(params.get("m_src_page"));
	if (sourcePage) {
		state.sourcePage = sourcePage;
	}

	const sourceLabel = cleanToken(params.get("m_src_label"));
	if (sourceLabel) {
		state.sourceLabel = sourceLabel;
	}

	const activeTab = cleanToken(params.get("m_tab"));
	if (activeTab) {
		state.activeTab = activeTab;
	}

	if (params.get("m_view") === "sheet") {
		state.presentation = "sheet";
	}

	const filter = params.get("m_filter");
	if (filter) {
		const separator = filter.indexOf(":");
		const filterKey = cleanToken(
			separator === -1 ? filter : filter.slice(0, separator),
			40,
		);
		const filterValue = cleanToken(
			separator === -1 ? undefined : filter.slice(separator + 1),
			120,
		);

		if (filterKey && filterValue !== undefined) {
			state.filterKey = filterKey;
			state.filterValue = filterValue;
		}
	}

	const scrollY = Number(params.get("m_scroll"));
	const cleanScroll = cleanScrollY(scrollY);
	if (cleanScroll !== undefined) {
		state.scrollY = cleanScroll;
	}

	const readonlyReason = cleanToken(params.get("m_readonly"), 120);
	if (readonlyReason) {
		state.readonlyReason = readonlyReason;
	}

	const archiveSeason = cleanToken(params.get("m_archive"), 40);
	if (archiveSeason) {
		state.archiveSeason = archiveSeason;
	}

	const returnUrl = cleanReturnUrl(params.get("m_return"));
	if (returnUrl) {
		state.returnHref = returnUrl;
		state.returnUrl = returnUrl;
		const returnLabel = cleanToken(params.get("m_return_label"));
		if (returnLabel) {
			state.returnLabel = returnLabel;
		}
	}

	return state;
};

export const mobileReturnStateToQuery = (state: MobileReturnState) => {
	const encoded = encodeMobileReturnState(state);
	return encoded ? `?${encoded}` : "";
};

export const appendMobileReturnStateToUrl = (
	url: string,
	state: MobileReturnState,
) => {
	const encoded = encodeMobileReturnState(state);
	if (!encoded) {
		return url;
	}

	const hashIndex = url.indexOf("#");
	const base = hashIndex === -1 ? url : url.slice(0, hashIndex);
	const hash = hashIndex === -1 ? "" : url.slice(hashIndex);
	const separator = base.includes("?")
		? base.endsWith("?") || base.endsWith("&")
			? ""
			: "&"
		: "?";

	return `${base}${separator}${encoded}${hash}`;
};

export const createMobileReturnUrl = (
	url: string,
	input: MobileReturnStateInput,
) => appendMobileReturnStateToUrl(url, createMobileReturnState(input));
